import React from 'react';
import { useTranslation } from 'react-i18next';

const Epsmcp = () => {
  const { t } = useTranslation('help');

  return (
    <div style={styles.container}>
      {/* Main Section Title */}
      <h2 style={styles.title}>
        {t('Epsmcp.title', 'Model Context Protocol Target Simulators:')}
      </h2>

      {/* Subsection Title */}
      <h3 style={styles.subTitle}>
        {t('Epsmcp.subTitle', '1. Secure MCP')}
      </h3>

      {/* Section (a) - Purpose */}
      <div style={styles.section}>
        <span style={styles.sectionLabel}>(a)</span>
        <div style={styles.sectionContent}>
          <p style={styles.paragraph}>
            <strong style={styles.strongText}>
              {t('Epsmcp.labels.purpose', 'Purpose:- ')}
            </strong>
            {t(
              'Epsmcp.descriptions.purpose',
              'Use an MCP Server Target when WhiteHaX should test a Model Context Protocol server that exposes tools, resources, prompts, or protocol methods to AI clients. Model Context Protocol (MCP) Prompts act as reusable, standardized message templates exposed by MCP servers to guide AI interactions. When testing prompts within an MCP architecture you evaluate both how the server declares the prompt and how it handles inputs and renders instructions to the agent.'
            )}
          </p>
        </div>
      </div>

      {/* Section (b) - Endpoint */}
      <div style={styles.section}>
        <span style={styles.sectionLabel}>(b)</span>
        <div style={styles.sectionContent}>
          <p style={styles.paragraph}>
            <strong style={styles.strongText}>
              {t('Epsmcp.labels.endpoint', 'Target :- ')}
            </strong>
            {t('Epsmcp.descriptions.endpoint',
              'Model Context Protocol (MCP) endpoints act as Model Context Protocol (MCP) targets act as standardized server URLs that allow AI prompts and models to dynamically discover and call external tools, data sources, and workflows'
            )}
          </p>
          <div style={styles.urlContainer}>
            <a 
              href="http://52.53.127.59:5000/mcp" 
              style={styles.link} 
              target="_blank" 
              rel="noopener noreferrer"
            >
              http://52.53.127.59:5000/mcp
            </a>
          </div>
        </div>
      </div>

      {/* Section (c) - Request json */}
      <div style={styles.section}>
        <span style={styles.sectionLabel}>(c)</span>
        <div style={styles.sectionContent}>
          <p style={styles.paragraph}>
            <strong style={styles.strongText}>
              {t('Epsmcp.labels.requestJson', 'Request json')}
            </strong>
          </p>
          <p style={styles.paragraph}>
            {t(
              'Epsmcp.descriptions.requestJson',
              'Defines how the prompt/input is sent to the target. Headers provide authentication/content information, request_template defines the request body, and prompt_path identifies the field containing the prompt.'
            )}
          </p>

          {/* Request JSON Code Block */}
          <div style={styles.codeBlock}>
            <div>{'{'}</div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"headers"</span>: {'{'}
            </div>
            <div style={styles.indent2}>
              <span style={styles.jsonKey}>"Authorization"</span>: <span style={styles.jsonString}>"Bearer xxx"</span>,
            </div>
            <div style={styles.indent2}>
              <span style={styles.jsonKey}>"Content-Type"</span>: <span style={styles.jsonString}>"application/json"</span>
            </div>
            <div style={styles.indent1}>{'}'},</div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"data_type"</span>: <span style={styles.jsonString}>"form"</span>,
            </div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"request_template"</span>: {'{'}
            </div>
            <div style={styles.indent2}>
              <span style={styles.jsonKey}>"user_input"</span>: <span style={styles.jsonString}>"{"{{PROMPT}}"}"</span>
            </div>
            <div style={styles.indent1}>{'}'},</div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"prompt_path"</span>:<span style={styles.jsonString}>"user_input"</span>
            </div>
            <div>{'}'}</div>
          </div>
        </div>
      </div>

      {/* Section (d) - Response Json */}
      <div style={styles.section}>
        <span style={styles.sectionLabel}>(d)</span>
        <div style={styles.sectionContent}>
          <p style={styles.paragraph}>
            <strong style={styles.strongText}>
              {t('Epsmcp.labels.responseJson', 'Reponse Json')}
            </strong>
          </p>
          <p style={styles.paragraph}>
            {t(
              'Epsmcp.descriptions.responseJson',
              'Defines the structure of the result returned by the target. It is used to capture the generated response, status, risk findings, scores, or other assessment information.'
            )}
          </p>

          {/* Response JSON Code Block */}
          <div style={styles.codeBlock}>
            <div>{'{'}</div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"category"</span>: <span style={styles.jsonString}>"input_output_risk"</span>,
            </div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"detected_at"</span>: <span style={styles.jsonString}>"2026-03-23T19:25:13.839740+00:00"</span>,
            </div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"evidence"</span>: {'{'}
            </div>
            <div style={styles.indent2}>
              <span style={styles.jsonKey}>"bias_hits"</span>: [],
            </div>
            <div style={styles.indent2}>
              <span style={styles.jsonKey}>"confidential_matches"</span>: [],
            </div>
            <div style={styles.indent2}>
              <span style={styles.jsonKey}>"pii_matches"</span>: [],
            </div>
            <div style={styles.indent2}>
              <span style={styles.jsonKey}>"toxicity_score"</span>: <span style={styles.jsonNumber}>0.05</span>
            </div>
            <div style={styles.indent1}>{'}'},</div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"evidence_note"</span>: [
            </div>
            <div style={styles.indent2}>
              <span style={styles.jsonString}>"No significant input/output risks detected."</span>,
            </div>
            <div style={styles.indent2}>
              <span style={styles.jsonString}>""</span>
            </div>
            <div style={styles.indent1}>],</div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"flags"</span>: [],
            </div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"recommended_actions"</span>: [],
            </div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"response"</span>: <span style={styles.jsonString}>"{"{{RESP}}"}"</span>,
            </div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"scores"</span>: {'{'}
            </div>
            <div style={styles.indent2}>
              <span style={styles.jsonKey}>"bias"</span>: <span style={styles.jsonNumber}>0.0</span>,
            </div>
            <div style={styles.indent2}>
              <span style={styles.jsonKey}>"confidential_leakage"</span>: <span style={styles.jsonNumber}>0.0</span>,
            </div>
            <div style={styles.indent2}>
              <span style={styles.jsonKey}>"pii_leakage"</span>: <span style={styles.jsonNumber}>0.0</span>,
            </div>
            <div style={styles.indent2}>
              <span style={styles.jsonKey}>"toxicity"</span>: <span style={styles.jsonNumber}>0.05</span>
            </div>
            <div style={styles.indent1}>{'}'},</div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"severity"</span>: <span style={styles.jsonNumber}>0.0125</span>,
            </div>
            <div style={styles.indent1}>
              <span style={styles.jsonKey}>"threat_detected"</span>: <span style={styles.jsonBoolean}>true</span>
            </div>
            <div>{'}'}</div>
          </div>

          {/* Expected Test Results */}
          <p style={styles.paragraph}>
            <strong style={styles.strongText}>
              {t('Epsmcp.labels.expectedResults', '(c) Expected Test Results against this simulator:- ')}
            </strong>
            {t(
              'Epsmcp.descriptions.expectedResults',
              'Since this simulator simulates secure MCP handling, the expected readiness result scores should be 90% or more across the applicable categories.'
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

// Component Styles (Dark Theme)
const styles = {
  container: {
    maxWidth: '850px',
    margin: '0 auto',
    padding: '24px',
    color: '#e0e0e0',
    backgroundColor: '#181a1f',
    borderRadius: '8px',
    fontFamily: 'Segoe UI, Roboto, sans-serif'
  },
  title: {
    margin: '0 0 15px 0',
    fontWeight: 'bold',
    color: '#ffffff',
    fontSize: '20px'
  },
  subTitle: {
    margin: '0 0 15px 0',
    fontWeight: 'bold',
    color: '#ffffff',
    fontSize: '16px'
  },
  section: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '15px'
  },
  sectionLabel: {
    width: '32px',
    flexShrink: 0,
    fontWeight: 'bold',
    color: '#a0a0a0'
  },
  sectionContent: {
    flexGrow: 1
  },
  paragraph: {
    margin: '0 0 10px 0',
    lineHeight: '1.6',
    color: '#cccccc',
    fontSize: '14px'
  },
  strongText: {
    color: '#ffffff'
  },
  urlContainer: {
    margin: '5px 0 15px 0'
  },
  link: {
    color: '#4fa3d1',
    textDecoration: 'underline'
  },
  codeBlock: {
    backgroundColor: '#0d0e11',
    color: '#e0e0e0',
    padding: '16px 20px',
    margin: '12px 0',
    borderRadius: '6px',
    border: '1px solid #2a2d34',
    fontFamily: 'Consolas, Monaco, "Andale Mono", monospace',
    fontSize: '13px',
    lineHeight: '1.5',
    overflowX: 'auto',
    whiteSpace: 'pre'
  },
  indent1: {
    paddingLeft: '20px'
  },
  indent2: {
    paddingLeft: '40px'
  },
  jsonKey: {
    color: '#6fc2ef'
  },
  jsonString: {
    color: '#e59373'
  },
  jsonNumber: {
    color: '#b5cea8'
  },
  jsonBoolean: {
    color: '#569cd6'
  }
};

export default Epsmcp;